import {
  always,
  applySpec,
  converge,
  ifElse,
  isNil,
  join,
  path,
  pathOr,
  includes,
  pipe,
  prop,
  filter,
  propOr,
  map,
  isEmpty,
  both,
  complement,
  anyPass,
  cond,
  equals,
  T,
  any,
  allPass,
  propEq,
  values,
  length,
  F,
  not,
  assoc,
  flatten,
  mergeAll,
  groupWith,
  when,
  unless,
  replace,
  sum,
  memoizeWith,
  curry,
  pathSatisfies,
  compose
} from 'ramda'

import { utils } from '@digital-commerce/shared-utils'

import { getCartType, getFirstMonthAddons, getProductName, getCacheKey } from '../cart'
import { buildErrorCodes, checkIsCartValid } from '../cartValidations'
import { getBYODOffers } from '../../components/organisms/Byod/utils'
import { getMobilityOffers, isEsim, isMobilePhone } from '../mobility'

const { getCustomerType } = utils.customer
const {
  getTopLevelItems,
  getMonthlyItemPrice,
  getItemOneTimePrice,
  getMonthlyAddons,
  getItemTotalFirstBill,
  isAffiliateFlow
} = utils.cart

const EPP_PRODUCT_OFFERING_ID = '9162917382539724602'

const hasFifaIrpcItems = (cart) => {
  return pipe(
    getTopLevelItems,
    any(allPass([propEq('provisioner', 'FIFA'), propEq('action', 'MODIFY')]))
  )(cart)
}

const getProductListType = ({ cart, isIrpcFlow }) => {
  return ifElse(
    anyPass([both(complement(isNil), hasFifaIrpcItems), always(isIrpcFlow)]),
    pipe(
      pathOr(['empty cart'], ['root', 'provisioners']),
      join(' | '),
      cond([
        [equals('COMPASS'), always('network: compass')],
        [equals('FIFA'), always('network: fifa')],
        [T, always('network: unspecified')]
      ])
    ),
    always(undefined)
  )(cart)
}

const getLobAndCustomerType = (cart) => {
  const customer = pathOr({}, ['root', 'customer'])(cart)
  const lineOfBusiness = getCartType(cart)
  const customerType = getCustomerType(customer)
  const lobAndCustomerTypeMap = {
    consumer: {
      mobility: 'mobility',
      hs: 'home solutions',
      mixed: 'mobility | home solutions'
    },
    business: {
      mobility: 'mobility | business',
      hs: 'home solutions | business',
      mixed: 'mobility | home solutions | business'
    },
    epp: {
      mobility: 'mobility | epp'
    }
  }

  const cartType = pathOr('not found', [customerType, lineOfBusiness])(lobAndCustomerTypeMap)
  return isAffiliateFlow(cart) ? `${cartType} | affiliate` : cartType
}

const buildDataLayerCartType = (cart) => {
  if (isNil(cart)) return 'error'

  const isCartEmpty = pathSatisfies(isEmpty, ['root', 'items'])
  if (isCartEmpty(cart)) return 'empty cart'

  return getLobAndCustomerType(cart)
}

const countMatchingItems = (cart, matcher) =>
  pipe(prop('items'), values, filter(matcher), length)(cart)

const getNumberOfRGUs = (cart) => {
  const _isRGU = (cartItem) => {
    return ifElse(
      propEq('category', 'MOBILITY_PLAN'),
      always(true),
      pipe(
        prop('tags'),
        cond([
          [includes('service'), T],
          [includes('collection'), T],
          [T, F]
        ])
      )
    )(cartItem)
  }

  return countMatchingItems(cart, _isRGU)
}

const getNumberOfItems = (category, cart) => {
  const hasCategory = (cartItem) => {
    return propEq('category', category)(cartItem)
  }

  return countMatchingItems(cart, hasCategory)
}

const getNumberOfPhones = (cart) => {
  const hasPhoneWithoutSmartWatch = (cartItem) => {
    return allPass([
      propEq('category', 'MOBILITY_PHONE'),
      pipe(propOr('', 'tags'), includes('smartwatch'), not)
    ])(cartItem)
  }

  return countMatchingItems(cart, hasPhoneWithoutSmartWatch)
}

const getNumberOfSmartWatches = (cart) => {
  const hasSmartwatch = (cartItem) => {
    return allPass([
      propEq('category', 'MOBILITY_PHONE'),
      pipe(propOr('', 'tags'), includes('smartwatch'))
    ])(cartItem)
  }

  return countMatchingItems(cart, hasSmartwatch)
}

const _commitmentTerm = pathOr('not core product', ['contractTerm', 'recurringDuration'])

export const replaceCommaForEppProductName = (itemName, sku) => {
  if (!itemName && itemName !== '') return null
  return EPP_PRODUCT_OFFERING_ID === sku ? itemName.replace(/,/g, '') : itemName
}

const mapSubItemsWithCommitmentTermAndOrderType = (items) => {
  const mobilityTopSubItems = ['MOBILITY_PLAN', 'MOBILITY_PHONE', 'MOBILITY_TABLET']

  const commitmentTermMap = pipe(
    filter(propEq('category', 'MOBILITY_OFFER')),
    map((item) => {
      const subItems = propOr([], 'items', item)
      const productOrderType = pathOr('', ['productOrder', 'type'])(item)
      const commitmentTermAndOrderType = {
        commitmentTerm: _commitmentTerm(item),
        productOrderType
      }

      return map((subItem) => assoc(subItem, commitmentTermAndOrderType, {}))(subItems)
    }),
    flatten,
    mergeAll
  )(items)

  const finalItems = map((item) => {
    if (mobilityTopSubItems.includes(item.category) && commitmentTermMap[item.id]) {
      const { commitmentTerm, productOrderType } = commitmentTermMap[item.id]
      return {
        ...item,
        contractTerm: {
          recurringDuration: commitmentTerm
        },
        productOrder: {
          type: productOrderType
        }
      }
    }
    if (item?.productOffering?.name === undefined) return item
    // eslint-disable-next-line no-param-reassign
    item.productOffering.name = replaceCommaForEppProductName(
      item.productOffering.name,
      item.productOffering.id
    )
    return item
  })(items)

  return finalItems
}

const getCustomer = pathOr({}, ['cart', 'root', 'customer'])

const getEasyPayment = (appState, mobilityOffer) => {
  const subItemIds = mobilityOffer.items
  const subItems = pipe(path(['cart', 'items']), pipe(values))(appState)

  const mobilityPhoneSubItem = subItems.filter(
    (subItem) => subItem.category === 'MOBILITY_PHONE' && subItemIds.includes(subItem.id)
  )

  return mobilityPhoneSubItem.length > 0
    ? applySpec({
        amount: prop('amount'),
        taxes: prop('taxes')
      })(getMonthlyItemPrice(mobilityPhoneSubItem[0]))
    : {
        amount: 0,
        taxes: []
      }
}

const applyEasyPayment = (appState) => (mobilityOffer) => {
  const easyPayment = getEasyPayment(appState, mobilityOffer)
  return assoc('easyPayment', easyPayment)(mobilityOffer)
}

const castItemPriceProps = (itemPrice) => {
  return {
    price: isNil(itemPrice?.price) ? 0 : itemPrice.price,
    taxes: isNil(itemPrice?.taxes) ? [] : itemPrice.taxes
  }
}

const getHsAddOnItemPrice = (item, defaultResult, monthlyItemPrice) => {
  const isMonthlyAddon = getMonthlyAddons([item]).length > 0
  const isFirstMonthAddon = getFirstMonthAddons([item]).length > 0
  // financed and included addons are billed monthly
  if (isMonthlyAddon)
    return {
      price: monthlyItemPrice?.subTotal,
      taxes: monthlyItemPrice?.taxes
    }

  // one time payment addons are charged to the first bill
  if (isFirstMonthAddon) {
    const itemPrice = getItemTotalFirstBill(item)
    return {
      price: itemPrice?.subTotal,
      taxes: itemPrice?.taxes
    }
  }

  return defaultResult
}

const getHsCreditItemPrice = (item) => {
  const itemPrice = path(['totalPrice', 'recurring', 0, 'recurringCreditBreakdowns', 0], item)
  return {
    price: itemPrice?.credit,
    taxes: itemPrice?.taxes
  }
}

const getHsItemPrice = (item) => {
  const defaultResult = {
    price: 0,
    taxes: []
  }
  const { tags } = item
  const itemOneTimePrice = getItemOneTimePrice(item)
  const monthlyItemPrice = getMonthlyItemPrice(item)
  const isAddon = includes('addon')(tags)

  if (isAddon) return getHsAddOnItemPrice(item, defaultResult, monthlyItemPrice)

  if (item.category === 'HS_CREDIT') {
    return getHsCreditItemPrice(item)
  }

  if (item.category === 'HS_GIFT' || item.tags.includes('gift')) return defaultResult

  // all other HS products should have a monthly price
  // or the one-time price
  if (monthlyItemPrice)
    return {
      price: monthlyItemPrice?.subTotal,
      taxes: monthlyItemPrice?.taxes
    }

  return {
    price: itemOneTimePrice?.subTotal,
    taxes: itemOneTimePrice?.taxes
  }
}

const getMobilityItemPrice = (item) => {
  const { category, easyPayment } = item
  const oneTimePrice = getItemOneTimePrice(item)
  const oneTimeSubTotal = propOr(0, 'subTotal')(oneTimePrice)
  const oneTimeCredit = propOr(0, 'credit')(oneTimePrice)
  const monthlyItemPrice = getMonthlyItemPrice(item)
  const monthlySubTotal = propOr(0, 'subTotal')(monthlyItemPrice)

  const mobilityItemPriceMap = {
    MOBILITY_ACCESSORY: oneTimeSubTotal,
    MOBILITY_PHONE: oneTimeSubTotal - oneTimeCredit,
    MOBILITY_PLAN: monthlySubTotal,
    MOBILITY_WARRANTY: monthlySubTotal,
    MOBILITY_CONNECTION_FEE: oneTimeSubTotal,
    MOBILITY_SIM: oneTimeSubTotal,
    MOBILITY_OFFER: easyPayment?.amount
  }

  const mobilityItemTaxesMap = {
    MOBILITY_ACCESSORY: oneTimePrice?.taxes,
    MOBILITY_PHONE: oneTimePrice?.taxes,
    MOBILITY_PLAN: monthlyItemPrice?.taxes,
    MOBILITY_WARRANTY: monthlyItemPrice?.taxes,
    MOBILITY_CONNECTION_FEE: oneTimePrice?.taxes,
    MOBILITY_SIM: oneTimePrice?.taxes,
    MOBILITY_OFFER: easyPayment?.taxes
  }

  const price = mobilityItemPriceMap[category]
  const taxes = mobilityItemTaxesMap[category]

  return { price: isNil(price) ? 'not found' : price, taxes }
}

const getItemPrice = (item) => {
  const { category } = item
  if (category.lastIndexOf('HS_', 0) === 0) return castItemPriceProps(getHsItemPrice(item))
  if (category.lastIndexOf('MOBILITY_', 0) === 0)
    return castItemPriceProps(getMobilityItemPrice(item))
  return 'not found'
}

const isAFinancedAccessory = (topLevelProduct) => {
  const isAnAccessory = propEq('category', 'MOBILITY_ACCESSORY')
  const hasRecurringPrice = pipe(pathOr([], ['itemPrice', 'recurring']), isEmpty, not)
  return allPass([isAnAccessory, hasRecurringPrice])(topLevelProduct)
}

const getOfferOrderType = (customer) => (cartItem) => {
  const provisioner = propOr('', 'provisioner')(cartItem)
  const productOrderType = isAFinancedAccessory(cartItem)
    ? 'FINANCE'
    : pathOr('', ['productOrder', 'type'])(cartItem)
  const customerType = getCustomerType(customer)

  const hsOrderTypesMap = {
    consumer: {
      ACQUISITION: 'hs_new',
      RECONTRACTING: 'hs_renew',
      UPSELL: 'hs_aal'
    },
    business: {
      ACQUISITION: 'bus_new',
      RECONTRACTING: 'bus_renew',
      UPSELL: 'bus_aal'
    }
  }

  const mobilityOrderTypesMap = {
    consumer: {
      ACTIVATION: 'mob_new',
      RENEWAL: 'mob_renew',
      AAL: 'mob_aal',
      OUTRIGHT_PURCHASE: 'mob_new',
      FINANCE: 'mob_accessories_financing'
    },
    business: {
      ACTIVATION: 'bus_new',
      RENEWAL: 'bus_renew',
      AAL: 'bus_aal',
      OUTRIGHT_PURCHASE: 'bus_new',
      FINANCE: 'bus_accessories_financing'
    },
    epp: {
      ACTIVATION: 'epp_new',
      RENEWAL: 'epp_renew',
      OUTRIGHT_PURCHASE: 'epp_new',
      FINANCE: 'epp_accessories_financing'
    }
  }

  if (['ESS', 'UPC'].includes(provisioner)) {
    return pathOr('not found', [customerType, productOrderType])(mobilityOrderTypesMap)
  }
  if (['FIFA', 'COMPASS'].includes(provisioner)) {
    return pathOr('not found', [customerType, productOrderType])(hsOrderTypesMap)
  }

  return 'not found'
}

const getTags = (item) => {
  const tags = prop('tags')(item)
  const category = prop('category')(item)
  const isMobility = category.lastIndexOf('MOBILITY_', 0) === 0
  if (tags.length < 1 && isMobility) return [category]
  return tags
}

const getCategory = (item) => {
  if (item.tags.includes('smartwear-device')) {
    return 'smartwear_security'
  }
  if (item.tags.includes('security') || item.tags.includes('smartwear')) {
    return 'smarthome_security'
  }
  return undefined
}

const addCategories = (items) => {
  const itemsWithCategories = items.map((item) => {
    const category = getCategory(item)
    if (category) {
      return {
        ...item,
        category
      }
    }
    return item
  })
  return itemsWithCategories
}

/**
 * Required for exception case where we want to track promotions as
 * part of the product list in the dataLayer.
 * Looks through item list for the specified discounts using the promotionId
 */
const appendPromotionsAsItems = (allItems, itemsAsValues, customer, promotionsToAppend) => {
  promotionsToAppend.forEach((promotionToAppend) => {
    const findPromotion = (item) => {
      /* Find promotion object within item object */
      let promotionItem

      const recurringItemPrices = item.itemPrice.recurring

      recurringItemPrices.find(({ alterations = [] }) =>
        alterations?.find((alteration) => {
          const foundPromotionAlteration = alteration?.promotionId === promotionToAppend.promotionId

          if (!foundPromotionAlteration) return false

          promotionItem = { ...alteration }

          return true
        })
      )

      return promotionItem
    }

    // See if item matches the product we're looking for by name then find promotion object within the item
    const isSpecifiedProduct = (item) =>
      item?.productOffering?.name === promotionToAppend.productName

    const specifiedProduct = itemsAsValues?.find(isSpecifiedProduct)

    if (!specifiedProduct) return

    // Find the promotion to append as an item
    const promotion = findPromotion(specifiedProduct)

    if (promotion) {
      // eslint-disable-next-line no-shadow
      const appendPromotionToCartItems = (allItems, { name, tags }) => [
        ...allItems,
        {
          name,
          commitmentTerm: 'not core product',
          offerOrderType: getOfferOrderType(customer)(specifiedProduct),
          price: 0,
          tags,
          quantity: 1
        }
      ]
      // eslint-disable-next-line no-param-reassign
      allItems = appendPromotionToCartItems(allItems, promotion)
    }
  })

  return allItems
}

/**
 * Retrieves the MOBILITY_SIM item associated with a mobility item.
 * @param {object} cart Cart object
 * @param {object} mobilityItem The item to retrieve the SIM item for
 * @returns {object} The MOBILITY_SIM item associated with the specified mobility item
 */
export const getMobilitySim = (cart, mobilityItem) => {
  const mobilityOffers = getMobilityOffers(cart)
  let mobilityOfferSubItemIds = []

  if (mobilityItem.category === 'MOBILITY_OFFER') {
    // mobilityItem is the offer (this is the case for BYOD)
    mobilityOfferSubItemIds = mobilityItem.items
  } else {
    // retrieve offer corresponding to mobilityItem (non-BYOD)
    const mobilityOffer = mobilityOffers?.find((offer) => offer.items?.includes(mobilityItem.id))
    mobilityOfferSubItemIds = mobilityOffer?.items
  }

  // Find the MOBILITY_SIM for the offer
  const cartItemsAsValues = Object.values(cart.items)
  const mobilitySimItem = cartItemsAsValues?.find(
    (item) => item.category === 'MOBILITY_SIM' && mobilityOfferSubItemIds?.includes(item.id)
  )
  return mobilitySimItem
}

const getSIMType = (appState) => (mobilityPhone) => {
  const mobilitySimItem = getMobilitySim(appState.cart, mobilityPhone)

  if (isEsim(mobilitySimItem)) return 'ESIM'

  return 'PSIM'
}

const applyBYODFlag = (appState) => (mobilityOffer) => {
  const byodOffers = getBYODOffers(appState.cart)
  if (byodOffers?.some((offer) => offer.id === mobilityOffer.id))
    return assoc('isBYODOffer', true)(mobilityOffer)
  return mobilityOffer
}

const buildProductList = (appState) => {
  const customer = getCustomer(appState)
  const itemsAsValues = pipe(path(['cart', 'items']), pipe(values))

  const baseSpec = {
    name: pipe(getProductName, unless(isNil, replace(/,/g, ''))),
    price: pipe(getItemPrice, prop('price')),
    offerOrderType: getOfferOrderType(customer),
    commitmentTerm: pathOr('not core product', ['contractTerm', 'recurringDuration']),
    tags: getTags,
    quantity: propOr(1, 'quantity')
  }

  let allItems = pipe(
    itemsAsValues,
    mapSubItemsWithCommitmentTermAndOrderType,
    map(
      pipe(
        // for MOBILITY_OFFER items, we need to apply the Easy Payment amount as the price
        // and attach a BYOD flag if the offer is BYOD to indicate deviceConfiguration should be added
        when(
          propEq('category', 'MOBILITY_OFFER'),
          compose(applyEasyPayment(appState), applyBYODFlag(appState))
        ),
        // for MOBILITY_PHONE and BYOD items, we apply simType to deviceConfiguration
        ifElse(
          anyPass([isMobilePhone, propEq('isBYODOffer', true)]),
          applySpec({
            ...baseSpec,
            deviceConfiguration: getSIMType(appState)
          }),
          applySpec(baseSpec)
        )
      )
    )
  )(appState)

  const promotionsToAddToProductList = [
    {
      productName: 'TELUS Online Security - Standard',
      promotionId: '9161040095713289868'
    },
    { productName: 'Coupon Holder', promotionId: '9163644627335645127' }
  ]

  allItems = addCategories(allItems)

  allItems = appendPromotionsAsItems(
    allItems,
    itemsAsValues(appState),
    customer,
    promotionsToAddToProductList
  )

  const groupedItems = groupWith(equals, allItems)
  const finalItemList = groupedItems.map((item) => {
    return mergeAll([
      item[0],
      { quantity: sum(map(prop('quantity'), item)) },
      { price: sum(map(prop('price'), item)) }
    ])
  })

  return finalItemList
}

export const createBaseEvent = memoizeWith(
  ({ cart }) => getCacheKey('legacy-analytics')(cart),
  ({ cart, lang, prov, location, isAuthenticated, isIrpcFlow, featureFlags, userUUID }) => {
    try {
      const getCart = prop('cart')
      const baseEvent = applySpec({
        page: {
          productList: getProductListType,
          name: always('commerce/cart'),
          province: path(['cart', 'root', 'province']),
          language: path(['cart', 'root', 'language']),
          type: always('dc cart'),
          brand: ifElse(
            pathSatisfies((type) => type === 'BUSINESS', ['cart', 'root', 'market', 'type']),
            always('BUSINESS'),
            always('CONSUMER')
          ),
          provisioner: pipe(pathOr(['empty cart'], ['cart', 'root', 'provisioners']), join(' | ')),
          personalizationApi: pathOr(false, ['featureFlags', 'personalizedPromoBlocks']),
          siteGenerator: () => 'UnifiedCommerceCheckout'
        },
        user: {
          uuid: pathOr('', ['userUUID'])
        },
        cart: {
          id: path(['cart', 'root', 'id']),
          numberOfRGUs: pipe(getCart, getNumberOfRGUs),
          numberOfAccessories: converge(getNumberOfItems, [always('MOBILITY_ACCESSORY'), getCart]),
          numberOfPhones: pipe(getCart, getNumberOfPhones),
          numberOfTablets: converge(getNumberOfItems, [always('MOBILITY_TABLET'), getCart]),
          numberOfWatches: pipe(getCart, getNumberOfSmartWatches),
          product: buildProductList,
          type: pipe(getCart, buildDataLayerCartType)
        },
        event: always({ cart: { action: 'view cart' } })
      })({ cart, lang, prov, location, isAuthenticated, isIrpcFlow, featureFlags, userUUID })

      if (!baseEvent.page.productList) delete baseEvent.page.productList

      return baseEvent
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      return {}
    }
  }
)

export const onPageLoadEvent = curry((linkType, inputs) => {
  const { cart, prov } = inputs
  const validationStatus = checkIsCartValid({ cart, prov, linkType })
  const baseEvent = createBaseEvent(inputs)

  if (!validationStatus.isValid) {
    const errorCodes = buildErrorCodes(cart)

    return {
      ...baseEvent,
      event: {
        ...baseEvent.event,
        error: {
          name: 'invalid cart',
          code: errorCodes || validationStatus?.reasons?.join(' | ')
        }
      }
    }
  }

  return baseEvent
})

export const onShowDetailsClick = curry((item, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'show details',
      details: pathOr('', ['productOffering', 'name'], item),
      action: 'click'
    }
  }
})

export const onKeepItemDialogClick = curry((item, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'keep cart item dialog box option selected',
      details: pathOr('', ['productOffering', 'name'], item),
      action: 'click'
    }
  }
})

export const onRemoveItemClick = curry((item, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'remove cart item dialog box option selected',
      details: pathOr('', ['productOffering', 'name'], item),
      action: 'click'
    }
  }
})

export const onCloseItemDialogClick = curry((item, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'cart removal dialog box closed',
      details: pathOr('', ['productOffering', 'name'], item),
      action: 'click'
    }
  }
})

export const onItemRemovalFailure = curry((itemRemovalHttpStatus, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      error: {
        name: 'cart removal error',
        code: itemRemovalHttpStatus
      }
    }
  }
})

export const onGiftReminder = (inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      error: {
        name: 'gift removed promo applied',
        code: 'gift removed promo applied'
      }
    }
  }
}

/* Promo code related events */
export const onPromoCodeApplied = curry((promoCode, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'promo code applied',
      action: 'click',
      details: promoCode
    }
  }
})

export const onPromoCodeInvalid = curry((promoCode, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'promo code does not exist',
      action: 'click',
      details: promoCode
    }
  }
})

export const onPromoCodeApplyFailure = curry((promoCode, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'code failed to apply',
      action: 'click',
      details: promoCode
    }
  }
})

export const onPromoCodeRemoveFailure = curry((promoCode, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'code failed to remove',
      action: 'click',
      details: promoCode
    }
  }
})

export const onPromoCodeRemoved = curry((promoCode, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'promo code removed',
      action: 'click',
      details: promoCode
    }
  }
})

export const onPromoCodePanelOpened = (inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'promo code panel opened',
      action: 'click',
      details: 'user clicked use promo code'
    }
  }
}

/* Error events */
export const onEmptyCart = (inputs) => {
  const baseEvent = createBaseEvent(inputs)
  const event = {
    ...baseEvent,
    page: { type: 'dc empty cart' },
    cart: {
      numberOfRGUs: 0,
      numberOfAccessories: 0,
      numberOfPhones: 0,
      numberOfTablets: 0,
      numberOfWatches: 0,
      product: []
    }
  }
  event.cart.type = undefined
  return event
}

export const onResolvingOfferError = (inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'invalid cart top level notification',
      details: 'add resolving offer'
    }
  }
}

export const onInvalidCartError = curry((errorCodes, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      error: {
        name: 'invalid cart',
        code: errorCodes
      }
    }
  }
})

const joinItemNames = (items) => {
  return items.map((item) => getProductName(item)).join(' | ')
}

export const onBackorderError = curry((items, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      name: 'Item on backorder',
      details: joinItemNames(items)
    }
  }
})

export const onOutOfStockError = curry((items, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      error: {
        name: 'Item unavailable',
        details: joinItemNames(items)
      }
    }
  }
})

export const onPersonalizationCallError = curry((errorMessage, inputs) => {
  const baseEvent = createBaseEvent(inputs)
  return {
    ...baseEvent,
    event: {
      error: {
        name: 'personalization api call error',
        details: errorMessage
      }
    }
  }
})

export const errorPageLoadDataLayerCreator = (lang, prov, errorPagePath) => {
  const url = new URL(window.location.href)
  const { pathname } = url
  // Removes the en/fr in the pathname from error page url
  const errorPageName = errorPagePath.match(/\/commerce(\/.*)$/)?.[0]

  return {
    page: {
      brand: pathname.includes('business') ? 'BUSINESS' : 'CONSUMER',
      language: lang,
      name: errorPageName ?? '',
      provisioner: 'not defined',
      province: prov,
      type: 'commerce error'
    },
    cart: {
      type: 'error'
    },
    event: {
      cart: { action: 'view cart' }
    }
  }
}
